/**
 * « Préparer l'année » (lot U5, ADR-0016) : la logique SANS écran.
 *
 * Le serveur calcule les propositions (échéances réglementaires de l'horizon) ; l'écran ne recalcule rien : il classe par mois,
 * filtre, coche et dit ce que le geste va faire AVANT de le faire. Rien ne part au serveur que des identifiants d'échéance et la
 * nature choisie (jamais présélectionnée : c'est un choix de l'utilisateur).
 */
import type { BilanPreparationAnnee, PropositionAnnee } from '../api/client';
import { jourFr } from './planning';

export type FiltreAnnee = 'A_CREER' | 'DEJA_PLANIFIEE' | 'A_COMPLETER' | 'TOUTES';

export const FILTRES_ANNEE: readonly { readonly valeur: FiltreAnnee; readonly libelle: string }[] = [
  { valeur: 'A_CREER', libelle: 'À créer' },
  { valeur: 'DEJA_PLANIFIEE', libelle: 'Déjà planifiées' },
  { valeur: 'A_COMPLETER', libelle: 'À compléter' },
  { valeur: 'TOUTES', libelle: 'Toutes' },
];

export const HORIZONS_RAPIDES: readonly number[] = [3, 6, 12, 24];

/** Ce que dit une échéance qu'on ne peut pas créer : la raison ET le geste qui la règle. */
export function phraseRaison(raison: PropositionAnnee['raison']): string {
  switch (raison) {
    case 'MACHINE_SANS_SITE':
      return 'La machine n’a pas de site : rattachez-la d’abord à un site dans Le Parc.';
    case 'MACHINE_ARCHIVEE':
      return 'La machine est archivée.';
    default:
      return '';
  }
}

/** « en retard de 12 jours », « aujourd’hui », « dans 1 jour ». */
export function phraseRetard(retardJours: number): string {
  if (retardJours === 0) return 'aujourd’hui';
  if (retardJours > 0) return `en retard de ${retardJours === 1 ? '1 jour' : `${retardJours} jours`}`;
  const reste = -retardJours;
  return `dans ${reste === 1 ? '1 jour' : `${reste} jours`}`;
}

export function filtrer(propositions: readonly PropositionAnnee[], filtre: FiltreAnnee, recherche: string): readonly PropositionAnnee[] {
  const mots = recherche
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .split(/\s+/)
    .filter((m) => m !== '');
  return propositions.filter((p) => {
    if (filtre !== 'TOUTES' && p.etat !== filtre) return false;
    if (mots.length === 0) return true;
    const texte = [p.regle, p.jalon, p.machine.libelle, p.machine.modele, p.machine.numeroSerie, p.machine.designationClient, p.site?.nom, p.site?.ville, p.client, p.organisme]
      .filter((t): t is string => typeof t === 'string')
      .join(' ')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();
    return mots.every((m) => texte.includes(m));
  });
}

const MOIS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];

/** « octobre 2026 » pour une clé « 2026-10 ». */
export function libelleMois(cle: string): string {
  const [annee, mois] = cle.split('-');
  const nom = MOIS[Number(mois) - 1];
  return nom && annee ? `${nom} ${annee}` : cle;
}

/**
 * Les propositions rangées par mois d'échéance, dans l'ordre. Les échéances DÉJÀ dépassées forment un groupe à part, en tête :
 * ce sont les plus urgentes et elles ne se perdent pas dans un mois passé.
 */
export function groupesParMois(
  propositions: readonly PropositionAnnee[],
  aujourdhui: string,
): readonly { readonly cle: string; readonly libelle: string; readonly propositions: readonly PropositionAnnee[] }[] {
  const retard = propositions.filter((p) => p.echeance < aujourdhui);
  const groupes = new Map<string, PropositionAnnee[]>();
  for (const p of propositions) {
    if (p.echeance < aujourdhui) continue;
    const cle = p.echeance.slice(0, 7);
    groupes.set(cle, [...(groupes.get(cle) ?? []), p]);
  }
  return [
    ...(retard.length > 0 ? [{ cle: 'retard', libelle: 'Déjà dépassées', propositions: retard }] : []),
    ...[...groupes.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([cle, liste]) => ({ cle, libelle: libelleMois(cle), propositions: liste })),
  ];
}

/** Le corps envoyé : la nature choisie et les identifiants cochés, rien d'autre — le serveur recalcule le reste. */
export function corpsCreation(natureId: string, cochees: ReadonlySet<string>): { natureId: string; obligationIds: string[] } {
  return { natureId, obligationIds: [...cochees] };
}

export type LectureCreation = { readonly ok: true } | { readonly ok: false; readonly erreur: string };

export function peutCreer(natureId: string, cochees: ReadonlySet<string>): LectureCreation {
  if (natureId === '') return { ok: false, erreur: 'Choisissez la nature des interventions à créer.' };
  if (cochees.size === 0) return { ok: false, erreur: 'Cochez au moins une échéance.' };
  return { ok: true };
}

/** Ce que le geste va faire, dit AVANT : combien d'interventions, à affecter, rien d'autre. */
export function phraseConfirmation(n: number, natureLibelle: string): string {
  const interventions = n === 1 ? '1 intervention' : `${n} interventions`;
  return (
    `Créer ${interventions} « ${natureLibelle} » ? Elles seront « à affecter » : sans jour ni technicien, avec une fenêtre qui finit à la date ` +
    'de l’échéance. Rien n’est planifié à votre place ; chacune se déplace, s’affecte ou s’annule ensuite.'
  );
}

/** Le bilan du geste : créées, refusées avec leur raison. */
export function phraseBilan(bilan: BilanPreparationAnnee): string {
  const creees = bilan.creees.length;
  const refusees = bilan.refusees.length;
  const c = creees === 0 ? 'Aucune intervention créée' : creees === 1 ? '1 intervention créée' : `${creees} interventions créées`;
  if (refusees === 0) return `${c}.`;
  return `${c} ; ${refusees === 1 ? '1 échéance refusée' : `${refusees} échéances refusées`} (la raison est dite en dessous).`;
}

export function phraseFenetre(debut: string, fin: string): string {
  return debut === fin ? `le ${jourFr(fin)}` : `du ${jourFr(debut)} au ${jourFr(fin)}`;
}
